import { useState, useEffect } from "react";
import {
  X, DollarSign, Calendar, User,
  FileText, Loader, TrendingDown,
  Briefcase, Percent, AlertCircle
} from "lucide-react";
import { createDeal, updateDeal } from "../api/services";
import { listUsers } from "../api/services";
import { useAuth } from "../Auth-Context/AuthContext";

// Updated stage values to match API expectations
const DEAL_STAGES = [
  { id: "PROSPECTING", name: "Prospecting", color: "#8B5CF6" },
  { id: "LEAD_QUALIFICATION", name: "Lead Qualification", color: "#EC4899" },
  { id: "DISCOVERY_PAIN_POINTS", name: "Discovery/Pain Points", color: "#F59E0B" },
  { id: "DEMO_PITCH", name: "Demo/Pitch", color: "#3B82F6" },
  { id: "PROPOSAL", name: "Proposal", color: "#10B981" },
  { id: "NEGOTIATION", name: "Negotiation", color: "#6366F1" },
  { id: "CLOSED_WON", name: "Closed Won", color: "#059669" },
  { id: "CLOSED_LOST", name: "Closed Lost", color: "#EF4444" },
  { id: "CUSTOMER", name: "Customer", color: "#009DB9" },
];

const CURRENCIES = ["KES", "USD", "EUR", "CFA France"];

function Modal({ open, onClose, children, width = "max-w-xl" }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${width} flex flex-col max-h-[90vh]`}>
        {children}
      </div>
    </div>
  );
}

export default function DealFormModal({ open, onClose, contact, deal, onSuccess }) {
  const isEdit = !!deal;
  const { canAssignTasksToOthers } = useAuth();

  const [form, setForm] = useState({
    title: "",
    contactId: "",
    assignedTo: "",
    value: "",
    currency: "KES",
    stage: "PROSPECTING",
    probability: 20,
    expectedCloseDate: "",
    description: "",
    lostReason: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const canAssignDeals = canAssignTasksToOthers();

  // Fetch all users ONLY when modal opens AND user has permission to assign deals
  useEffect(() => {
    if (open && canAssignDeals) {
      fetchAllUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canAssignDeals]);

  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    setError(""); // Clear any previous errors
    try {
      const response = await listUsers({ page: 0, size: 100 });
      const allUsers = response.content || [];
      setUsers(allUsers);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      // Don't show error for sales reps since they shouldn't see this anyway
      if (canAssignDeals) {
        setError("Failed to load users");
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (isEdit && deal) {
        setForm({
          title: deal.title || "",
          contactId: deal.contactId || contact?.id || "",
          assignedTo: deal.assignedTo || "",
          value: deal.value || "",
          currency: deal.currency || "KES",
          stage: deal.stage || "PROSPECTING",
          probability: deal.probability ?? 20,
          expectedCloseDate: deal.expectedCloseDate || "",
          description: deal.description || "",
          lostReason: deal.lostReason || "",
        });
      } else {
        setForm({
          title: "",
          contactId: contact?.id || "",
          assignedTo: "",
          value: "",
          currency: "KES",
          stage: "PROSPECTING",
          probability: 20,
          expectedCloseDate: "",
          description: "",
          lostReason: "",
        });
      }
      setError("");
    }
  }, [open, deal, contact, isEdit]);

  const set = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const handleStageSelect = (stageId) => {
    const probMap = {
      PROSPECTING: 20,
      LEAD_QUALIFICATION: 30,
      DISCOVERY_PAIN_POINTS: 40,
      DEMO_PITCH: 50,
      PROPOSAL: 60,
      NEGOTIATION: 75,
      CLOSED_WON: 100,
      CLOSED_LOST: 0,
      CUSTOMER: 100,
    };
    set("stage", stageId);
    set("probability", probMap[stageId] ?? form.probability);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      setError("Deal title is required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        title: form.title.trim(),
        contactId: form.contactId,
        assignedTo: form.assignedTo || undefined,
        value: form.value !== "" ? parseFloat(form.value) : 0,
        currency: form.currency,
        stage: form.stage,
        probability: parseInt(form.probability) || 0,
        expectedCloseDate: form.expectedCloseDate || undefined,
        description: form.description.trim() || undefined,
        lostReason: form.stage === "CLOSED_LOST" ? form.lostReason.trim() : undefined,
      };

      if (isEdit) {
        await updateDeal(deal.id, payload);
      } else {
        await createDeal(payload);
      }

      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save deal. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const selectedStage = DEAL_STAGES.find((s) => s.id === form.stage);

  return (
    <Modal open={open} onClose={onClose} width="max-w-lg">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0 px-6 py-5 border-b border-neutral-100">
        <div>
          <h3 className="text-[15px] font-bold text-neutral-900">
            {isEdit ? "Edit Deal" : "New Deal"}
          </h3>
          {contact && (
            <p className="text-[11px] text-neutral-400 mt-0.5">
              {isEdit ? "Editing deal for" : "Creating deal for"}{" "}
              <span className="font-semibold text-neutral-600">{contact.company_name}</span>
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center rounded-lg w-7 h-7 text-neutral-400 hover:bg-neutral-100"
        >
          <X size={15} />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 gap-4 px-6 py-5 overflow-y-auto">

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-[12px] text-red-600">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        {/* Title */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
            Deal Title *
          </label>
          <div className="relative">
            <Briefcase className="absolute left-3 top-2.5 text-neutral-400" size={14} />
            <input
              type="text"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. Enterprise Software License"
              className="w-full h-9 pl-9 pr-4 border border-neutral-200 rounded-lg text-[13px] outline-none focus:border-[#009DB9] transition-colors"
            />
          </div>
        </div>

        {/* Stage Selector */}
        <div className="flex flex-col gap-2">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
            Stage
          </label>
          <div className="grid grid-cols-3 gap-2">
            {DEAL_STAGES.map((stage) => (
              <button
                key={stage.id}
                onClick={() => handleStageSelect(stage.id)}
                className="py-2 px-3 rounded-lg border text-[11px] font-semibold transition-all flex items-center gap-1.5"
                style={
                  form.stage === stage.id
                    ? { background: `${stage.color}15`, borderColor: stage.color, color: stage.color }
                    : { background: "#fff", borderColor: "#e5e5e5", color: "#737373" }
                }
              >
                <span
                  className="flex-shrink-0 w-2 h-2 rounded-full"
                  style={{ background: stage.color }}
                />
                {stage.name}
              </button>
            ))}
          </div>
        </div>

        {/* Value + Currency */}
        <div className="grid grid-cols-3 gap-3">
          <div className="flex flex-col col-span-2 gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
              Deal Value
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 text-neutral-400" size={14} />
              <input
                type="number"
                value={form.value}
                onChange={(e) => set("value", e.target.value)}
                placeholder="0"
                className="w-full h-9 pl-9 pr-4 border border-neutral-200 rounded-lg text-[13px] outline-none focus:border-[#009DB9] transition-colors"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
              Currency
            </label>
            <select
              value={form.currency}
              onChange={(e) => set("currency", e.target.value)}
              className="h-9 border border-neutral-200 rounded-lg px-3 text-[13px] outline-none focus:border-[#009DB9] transition-colors bg-white"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Probability + Expected Close Date */}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider flex items-center gap-1">
              <Percent size={10} /> Probability
            </label>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={100}
                value={form.probability}
                onChange={(e) => set("probability", e.target.value)}
                className="w-full h-9 px-3 border border-neutral-200 rounded-lg text-[13px] outline-none focus:border-[#009DB9] transition-colors"
              />
            </div>
            <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className="h-full transition-all rounded-full"
                style={{
                  width: `${Math.min(100, Math.max(0, form.probability))}%`,
                  background: selectedStage?.color || "#009DB9"
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider flex items-center gap-1">
              <Calendar size={10} /> Expected Close
            </label>
            <input
              type="date"
              value={form.expectedCloseDate}
              onChange={(e) => set("expectedCloseDate", e.target.value)}
              className="h-9 border border-neutral-200 rounded-lg px-3 text-[12px] outline-none focus:border-[#009DB9] transition-colors"
            />
          </div>
        </div>

        {/* Assigned To - Dropdown with all users (only for those with permission) */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
            Assigned To
            {!canAssignDeals && (
              <span className="ml-2 text-xs font-normal text-neutral-400">
                (Only MANAGER, ADMIN, SUPER_ADMIN can assign deals)
              </span>
            )}
          </label>
          <div className="relative">
            <User className="absolute left-3 top-2.5 text-neutral-400" size={14} />
            {canAssignDeals ? (
              <select
                value={form.assignedTo}
                onChange={(e) => set("assignedTo", e.target.value)}
                className="w-full h-9 pl-9 pr-4 border border-neutral-200 rounded-lg text-[13px] outline-none focus:border-[#009DB9] transition-colors bg-white"
                disabled={loadingUsers}
              >
                <option value="">Unassigned</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.fullName || `${user.firstName} ${user.lastName}`}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value="You cannot assign deals"
                disabled
                className="w-full h-9 pl-9 pr-4 border border-neutral-200 rounded-lg text-[13px] outline-none bg-neutral-50 text-neutral-400 cursor-not-allowed"
              />
            )}
          </div>
          {loadingUsers && canAssignDeals && (
            <p className="text-[11px] text-neutral-400 mt-1">Loading users...</p>
          )}
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
            Description
          </label>
          <div className="relative">
            <FileText className="absolute left-3 top-2.5 text-neutral-400" size={14} />
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="What is this deal about?"
              className="w-full pl-9 pr-4 pt-2 pb-2 border border-neutral-200 rounded-lg text-[13px] outline-none focus:border-[#009DB9] resize-none transition-colors"
            />
          </div>
        </div>

        {/* Lost Reason — only shown when stage is CLOSED_LOST */}
        {form.stage === "CLOSED_LOST" && (
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-red-500 uppercase tracking-wider flex items-center gap-1">
              <TrendingDown size={10} /> Reason for Loss
            </label>
            <textarea
              rows={2}
              value={form.lostReason}
              onChange={(e) => set("lostReason", e.target.value)}
              placeholder="e.g. Budget constraints, competitor chosen, timing..."
              className="w-full border border-red-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-red-400 resize-none transition-colors bg-red-50/30"
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between flex-shrink-0 px-6 py-4 border-t border-neutral-100">
        <button
          onClick={onClose}
          className="px-5 py-2 text-[13px] font-medium text-neutral-600 hover:bg-neutral-100 rounded-xl transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || !form.title.trim()}
          className="px-6 py-2 text-[13px] font-semibold text-white rounded-xl disabled:opacity-40 flex items-center gap-2 transition-colors"
          style={{ background: "#009DB9" }}
          onMouseEnter={(e) => { if (!saving && form.title) e.currentTarget.style.background = "#007a90"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#009DB9"; }}
        >
          {saving && <Loader size={14} className="animate-spin" />}
          {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Deal"}
        </button>
      </div>
    </Modal>
  );
}